import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Check, ShieldCheck, Truck, Heart, Star, Plus, PiggyBank, Loader2, Lock, CreditCard, RotateCcw, PackageCheck } from "lucide-react";
import api from "@/lib/api";
import HowItWorks from "@/components/HowItWorks";

const openEstimate = () => window.dispatchEvent(new Event("open-estimate-modal"));

const PETS = [
  "https://images.unsplash.com/photo-1517849845537-4d257902454a?auto=format&fit=crop&w=400&q=80",
  "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&w=400&q=80",
  "https://images.unsplash.com/photo-1561037404-61cd46aa615b?auto=format&fit=crop&w=400&q=80",
  "https://images.unsplash.com/photo-1574144611937-0df059b5ef3e?auto=format&fit=crop&w=400&q=80",
  "https://images.unsplash.com/photo-1518717758536-85ae29035b6d?auto=format&fit=crop&w=400&q=80",
];

const MEDS = [
  { name: "Advair MDI", tag: "Asthma & airways", img: "/meds/advair.png" },
  { name: "Entresto", tag: "Heart health", img: "/meds/entresto.png" },
  { name: "Flovent HFA", tag: "Asthma & airways", img: "/meds/flovent.png" },
  { name: "Spironolactone", tag: "Heart health", img: "/meds/spironolactone.png" },
];

const VALUES = [
  { icon: PiggyBank, title: "Up to 30% savings", desc: "Real time price comparison across licensed pharmacies, with no hidden fees, ever." },
  { icon: ShieldCheck, title: "Vet reviewed & verified", desc: "Every prescription is approved by a licensed Canadian vet before any pharmacy fills it." },
  { icon: Truck, title: "100% online & delivered", desc: "No pharmacy visits. Upload securely from home and we ship it across the United States." },
];

const TESTIMONIALS = [
  { quote: "Bella's allergy meds were $210 locally. PawLinx found them for $58, vet approved, at my door in days.", name: "Sarah M.", who: "Bella's mom · Columbus, OH", img: "https://images.unsplash.com/photo-1552053831-71594a27632d?auto=format&fit=crop&w=600&q=80", av: "https://i.pravatar.cc/80?img=32" },
  { quote: "It felt completely legitimate. A real vet reviewed the prescription and the pricing was upfront. No surprises.", name: "James T.", who: "Max's dad · Austin, TX", img: "https://images.unsplash.com/photo-1568572933382-74d440642117?auto=format&fit=crop&w=600&q=80", av: "https://i.pravatar.cc/80?img=12" },
  { quote: "Getting an estimate took two minutes. The refill reminders mean I never run out of Luna's heart meds. Brilliant.", name: "Priya K.", who: "Luna's mom · Seattle, WA", img: "https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=600&q=80", av: "https://i.pravatar.cc/80?img=45" },
];

const FAQS = [
  { q: "Is PawLinx a pharmacy?", a: "No. PawLinx is a referral service. We compare prices, arrange a licensed vet's review, and connect you with a licensed Canadian pharmacy that fills and ships your order." },
  { q: "How much can I really save?", a: "Most pet parents save up to 30% versus their local pharmacy. You'll see the exact comparison before you commit to anything." },
  { q: "Do I need a prescription from my vet?", a: "Yes. You upload your existing prescription through a secure one time link. A licensed Canadian vet then verifies it before the pharmacy fills your order." },
  { q: "Where do you deliver?", a: "Across the United States. Orders ship via tracked delivery once approved and filled, with status updates every step of the way." },
];

const Stars = ({ className = "" }) => (
  <div className={`flex gap-0.5 text-paw-gold ${className}`}>
    {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
  </div>
);

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <div className="max-w-3xl mx-auto mt-12 space-y-3">
      {FAQS.map((f, i) => (
        <div key={f.q} className="bg-white border border-paw-border rounded-2xl overflow-hidden shadow-sm">
          <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left">
            <span className="font-semibold text-paw-navy text-base sm:text-lg">{f.q}</span>
            <span className={`shrink-0 w-6 h-6 grid place-items-center rounded-full border transition-all ${open === i ? "bg-paw-primary border-paw-primary text-white rotate-45" : "border-paw-border text-paw-primary"}`}>
              <Plus className="w-4 h-4" />
            </span>
          </button>
          {open === i && <div className="px-5 sm:px-6 pb-5 text-paw-text-secondary text-[15px]">{f.a}</div>}
        </div>
      ))}
    </div>
  );
}

function HeroSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/medications/search?q=${encodeURIComponent(q)}&limit=8`);
        setResults(data.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q]);

  const pick = (r) => {
    setSelected(r);
    setQ([r.name, r.dosage_form].filter(Boolean).join(" ") + (r.strength ? ` - ${r.strength}` : ""));
    setMenuOpen(false);
  };

  // Only fire the "we can source it" lead popup when the customer has finished and
  // submitted (Enter / Get estimate) or explicitly clicked the request link — never
  // mid-typing, so partial entries like "aced" don't trigger it prematurely.
  const requestMed = (term) => {
    const t = (term || "").trim();
    if (t.length < 3) return;
    window.dispatchEvent(new CustomEvent("open-med-not-found", { detail: { medication: t } }));
    setMenuOpen(false);
  };

  const go = () => {
    const term = q.trim();
    if (selected) { window.dispatchEvent(new CustomEvent("open-estimate-modal", { detail: { med: selected } })); return; }
    if (term && !searching && results.length === 0) { requestMed(term); return; }
    if (term) window.dispatchEvent(new CustomEvent("open-estimate-modal", { detail: { query: term } }));
    else window.dispatchEvent(new Event("open-estimate-modal"));
  };

  return (
    <div className="mt-7 max-w-lg relative">
      <div className="flex items-center gap-2 bg-white border border-paw-border rounded-full pl-5 pr-2 py-2 shadow-[0_14px_36px_-16px_rgba(11,39,71,0.22)]">
        <Search className="w-5 h-5 text-paw-text-muted shrink-0" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setSelected(null); setMenuOpen(true); }}
          onFocus={() => setMenuOpen(true)}
          onBlur={() => setTimeout(() => setMenuOpen(false), 160)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } }}
          placeholder="Search your pet's medication..."
          className="flex-1 min-w-0 bg-transparent outline-none text-base text-paw-text placeholder:text-paw-text-muted"
        />
        <button onClick={go} className="bg-paw-primary hover:bg-paw-primary-hover text-white rounded-full px-4 sm:px-5 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap">
          <span className="sm:hidden">Get estimate</span>
          <span className="hidden sm:inline">Get an instant estimate</span>
        </button>
      </div>
      {menuOpen && q.trim().length >= 2 && (
        <div className="absolute z-50 left-0 right-0 mt-2 max-h-72 overflow-y-auto bg-white border border-paw-border rounded-2xl shadow-xl">
          {searching && (
            <div className="px-4 py-3 text-sm text-paw-text-muted flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</div>
          )}
          {!searching && results.length === 0 && (
            <div className="px-4 py-3">
              <p className="text-sm text-paw-text-muted">Nothing found for "{q}".</p>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => requestMed(q.trim())}
                className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-paw-primary hover:underline"
              >
                We can still get it. Request an estimate
              </button>
            </div>
          )}
          {!searching && results.map((r) => (
            <button
              key={`${r.name}-${r.din}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(r)}
              className="w-full text-left px-4 py-2.5 hover:bg-paw-bg transition-colors border-b border-paw-border/40 last:border-0"
            >
              <p className="text-sm text-paw-text truncate">{[r.name, r.dosage_form].filter(Boolean).join(" ")}{r.strength ? ` - ${r.strength}` : ""}</p>
              {r.din && <p className="text-[11px] text-paw-text-muted">DIN {r.din}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="bg-paw-cream">
      <style>{"@keyframes pawmarquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}"}</style>

      {/* HERO */}
      <section className="relative overflow-x-clip">
        <div className="pointer-events-none absolute -right-40 -top-28 w-[560px] h-[560px] rounded-full" style={{ background: "radial-gradient(circle, rgba(15,98,214,0.10), transparent 62%)" }} />
        <div className="relative max-w-[1180px] mx-auto px-6 pt-12 pb-16 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">
              <Heart className="w-4 h-4 fill-current" /> Pet medication referral · United States
            </span>
            <h1 className="mt-4 text-[40px] sm:text-[52px] lg:text-[62px] leading-[1.04] font-extrabold tracking-tight text-paw-navy">
              Pay less for the meds your{" "}
              <span className="relative inline-block text-paw-primary">
                pet
                <svg className="absolute left-0 -bottom-2 w-full h-3" viewBox="0 0 90 14" preserveAspectRatio="none" aria-hidden="true"><path d="M3 9 C 25 3, 45 3, 64 8 S 84 11, 87 5" fill="none" stroke="#0F62D6" strokeWidth="5" strokeLinecap="round" /></svg>
              </span>{" "}
              needs.
            </h1>
            <p className="mt-6 text-lg text-paw-text-secondary max-w-xl">
              PawLinx hunts down the lowest price on your pet's prescription from affiliated Canadian pharmacies, reviewed by real vets and shipped to your door across the United States. We do the price shopping. You keep the savings.
            </p>

            <HeroSearch />
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
              {["Free & no commitment", "Vet reviewed", "US delivery"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-paw-text-secondary"><Check className="w-4 h-4 text-paw-success" /> {t}</span>
              ))}
            </div>

            <div className="mt-7 flex items-center gap-3.5">
              <div className="flex">
                {[12, 32, 5, 45].map((n, i) => (
                  <span key={n} className="w-9 h-9 rounded-full border-[2.5px] border-paw-cream bg-cover bg-center shadow-sm" style={{ marginLeft: i === 0 ? 0 : -11, backgroundImage: `url(https://i.pravatar.cc/80?img=${n})` }} />
                ))}
              </div>
              <div>
                <Stars />
                <p className="text-[12.5px] text-paw-text-muted mt-0.5"><span className="font-semibold text-paw-text">Trusted by 12,000+ pet parents</span></p>
              </div>
            </div>
          </div>

          {/* Collage */}
          <div className="relative h-[420px] sm:h-[500px]">
            <svg className="absolute right-[2%] -top-3 w-10 h-10 z-10" viewBox="0 0 40 40" aria-hidden="true"><path d="M20 2 L23 16 L37 20 L23 24 L20 38 L17 24 L3 20 L17 16 Z" fill="#EBB13E" /></svg>
            <svg className="absolute left-[34%] -bottom-2 w-6 h-6 z-10" viewBox="0 0 40 40" aria-hidden="true"><path d="M20 4 L22 17 L35 20 L22 23 L20 36 L18 23 L5 20 L18 17 Z" fill="#0F62D6" /></svg>
            <svg className="absolute -left-3 top-[40%] w-16 h-8 z-10" viewBox="0 0 60 30" aria-hidden="true"><path d="M4 22 C 16 6, 40 6, 56 18" fill="none" stroke="#0F62D6" strokeWidth="3.5" strokeLinecap="round" strokeDasharray="1 8" /></svg>

            <div className="absolute right-0 top-[4%] w-[60%] h-[74%] rounded-3xl border-[5px] border-white shadow-[0_40px_90px_-34px_rgba(11,39,71,0.45)] bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?auto=format&fit=crop&w=900&q=80)" }} />
            <div className="absolute left-0 bottom-[2%] w-[46%] h-[46%] rounded-3xl border-[5px] border-white shadow-[0_40px_90px_-34px_rgba(11,39,71,0.45)] bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1574158622682-e40e69881006?auto=format&fit=crop&w=700&q=80)" }} />
            <div className="absolute left-[2%] top-0 w-[34%] h-[34%] rounded-3xl border-[5px] border-white shadow-[0_40px_90px_-34px_rgba(11,39,71,0.45)] bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1543466835-00a7907e9de1?auto=format&fit=crop&w=600&q=80)" }} />

            <div className="absolute right-[6%] top-0 z-20 bg-white rounded-full px-3.5 py-2 shadow-lg flex items-center gap-2 text-[13.5px] font-semibold text-paw-navy"><Check className="w-4 h-4 text-paw-success" /> Vet reviewed</div>
            <div className="absolute left-0 top-[42%] z-20 bg-paw-navy text-white rounded-full px-3.5 py-2 shadow-lg text-[13.5px] font-semibold">Save up to <span className="text-[#7FE3FF]">30%</span></div>
            <div className="absolute right-[-2%] bottom-[8%] z-20 bg-white rounded-full px-3.5 py-2 shadow-lg flex items-center gap-2 text-[13.5px] font-semibold text-paw-navy"><Stars /> 4.9/5</div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-paw-navy text-white/80 overflow-hidden py-4">
        <div className="flex whitespace-nowrap w-max" style={{ animation: "pawmarquee 26s linear infinite" }}>
          {[0, 1].map((dup) => (
            ["Affiliated Canadian pharmacies", "Affiliated licensed vets", "US tracked delivery", "Transparent pricing, no hidden fees"].map((t) => (
              <span key={t + dup} className="inline-flex items-center gap-2.5 text-[15px] font-medium mr-12"><Check className="w-[18px] h-[18px] text-[#74E0AE]" /> {t}</span>
            ))
          ))}
        </div>
      </div>

      {/* WHY PAWLINX — lowest-price guarantee + giving back */}
      <section className="bg-paw-cream py-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">Why PawLinx</span>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-extrabold tracking-tight text-paw-navy">Lower prices. Bigger hearts.</h2>
          </div>
          <div className="mt-11 grid lg:grid-cols-2 gap-5">
            {/* Lowest price guarantee */}
            <div className="bg-white border border-paw-border rounded-3xl p-8 shadow-sm hover:shadow-lg hover:border-paw-primary/30 transition-all">
              <div className="w-14 h-14 rounded-2xl grid place-items-center bg-paw-blue-50 text-paw-primary mb-5"><PiggyBank className="w-7 h-7" /></div>
              <h3 className="text-xl sm:text-2xl font-extrabold text-paw-navy mb-2">Guaranteed lowest prices in the market</h3>
              <p className="text-[15px] text-paw-text-secondary leading-relaxed">We compare licensed pharmacies in real time so you always pay the lowest price on your pet's medication, with no hidden fees.</p>
              <p className="mt-3 text-[12px] text-paw-text-muted">Conditions apply.</p>
              <button onClick={openEstimate} className="mt-5 inline-flex items-center gap-1.5 text-paw-primary font-semibold text-sm">Check your price <ArrowRight className="w-4 h-4" /></button>
            </div>
            {/* Giving back */}
            <div className="relative overflow-hidden bg-paw-navy text-white rounded-3xl p-8 shadow-sm">
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/5" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl grid place-items-center bg-white/10 text-paw-gold mb-5"><Heart className="w-7 h-7 fill-current" /></div>
                <h3 className="text-xl sm:text-2xl font-extrabold mb-2">Every order helps a pet who needs surgery</h3>
                <p className="text-[15px] text-white/75 leading-relaxed">PawLinx donates a portion of what you pay to help fund life saving surgeries for pets whose families cannot afford them. You save on your pet's meds, and together we give another pet a second chance.</p>
                <Link to="/about" className="mt-5 inline-flex items-center gap-1.5 text-paw-gold font-semibold text-sm">Read our story <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SAVINGS STATEMENT */}
      <section className="bg-paw-cream-2 py-20 sm:py-24 text-center">
        <div className="max-w-[1180px] mx-auto px-6">
          <span className="text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">The PawLinx difference</span>
          <h2 className="font-slab mt-4 text-3xl sm:text-5xl leading-tight max-w-4xl mx-auto text-paw-navy">
            Pet parents save up to <span className="text-paw-primary">30%</span> on the exact same medication.
          </h2>
          <div className="mt-11 flex flex-wrap gap-4 justify-center">
            {PETS.map((src, i) => (
              <div key={src} className={`w-24 h-24 sm:w-28 sm:h-28 rounded-3xl border-[5px] border-white shadow-md bg-cover bg-center ${i % 2 ? "rotate-3" : "-rotate-3"}`} style={{ backgroundImage: `url(${src})` }} />
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — Apple-style scroll-driven storytelling (real product screens) */}
      <HowItWorks />

      {/* POPULAR MEDS */}
      <section id="meds" className="bg-paw-cream py-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <span className="text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">Popular right now</span>
          <h2 className="mt-3 text-3xl sm:text-[40px] font-extrabold tracking-tight text-paw-navy">Medications pet parents save on most</h2>
          <p className="mt-3 text-lg text-paw-text-secondary max-w-2xl">A quick look at what others are getting for less. Search yours for an exact estimate.</p>
          <div className="mt-11 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {MEDS.map((m) => (
              <button key={m.name} onClick={openEstimate} className="text-left bg-white border border-paw-border rounded-3xl overflow-hidden shadow-sm hover:-translate-y-1.5 hover:shadow-lg transition-all">
                <div className="relative aspect-square bg-white">
                  <img src={m.img} alt={m.name} loading="lazy" className="absolute inset-0 w-full h-full object-contain p-4 sm:p-6" />
                  <span className="absolute left-3 top-3 bg-white text-paw-navy font-semibold text-[11.5px] px-2.5 py-1 rounded-full shadow-sm">{m.tag}</span>
                </div>
                <div className="p-5 border-t border-paw-border">
                  <h3 className="text-[17px] font-bold text-paw-navy leading-tight">{m.name}</h3>
                </div>
              </button>
            ))}
          </div>
          <div className="mt-9 flex justify-center">
            <button onClick={openEstimate} className="inline-flex items-center gap-2 bg-paw-accent hover:bg-paw-accent-hover text-white rounded-full px-7 py-3.5 font-manrope font-semibold text-[15px] shadow-lg shadow-paw-accent/20 transition-colors" data-testid="popular-meds-estimate">
              Get your instant estimate now <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white py-20">
        <div className="max-w-[1180px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-[32px] overflow-hidden shadow-[0_40px_90px_-34px_rgba(11,39,71,0.45)] aspect-square bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1450778869180-41d0601e046e?auto=format&fit=crop&w=900&q=80)" }}>
            <svg className="absolute right-[-6px] top-[-6px] w-12 h-12" viewBox="0 0 40 40" aria-hidden="true"><path d="M20 2 L23 16 L37 20 L23 24 L20 38 L17 24 L3 20 L17 16 Z" fill="#EBB13E" /></svg>
          </div>
          <div>
            <span className="text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">Why PawLinx</span>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-extrabold tracking-tight text-paw-navy">We're not a pharmacy. We're your shortcut to a better price.</h2>
            <p className="mt-4 text-[17px] text-paw-text-secondary">PawLinx is a referral service. We compare licensed pharmacies, arrange a vet's review, and connect you to the lowest price. Simple, safe, and squarely on your side.</p>
            <div className="mt-8 flex flex-col gap-6">
              {VALUES.map((v) => (
                <div key={v.title} className="flex gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-2xl grid place-items-center bg-paw-blue-50 text-paw-primary"><v.icon className="w-6 h-6" /></div>
                  <div>
                    <h3 className="text-lg font-bold text-paw-navy mb-1">{v.title}</h3>
                    <p className="text-[14.5px] text-paw-text-secondary">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GIVE BACK */}
      <section className="bg-paw-cream-2 py-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center bg-white rounded-[32px] border border-paw-border p-8 sm:p-12 shadow-sm">
            <div>
              <span className="inline-flex items-center gap-2 text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary"><Heart className="w-4 h-4 fill-current" /> Paws that give back</span>
              <h2 className="mt-3 text-3xl sm:text-[38px] font-extrabold tracking-tight text-paw-navy">Every order helps a pet in need</h2>
              <p className="mt-4 text-[17px] text-paw-text-secondary">A portion of every purchase goes to nonprofits that fund life saving surgeries for pets whose families cannot afford them. You save on your pet's meds, and together we help another pet get a second chance.</p>
              <div className="mt-6 flex items-center gap-2 text-paw-navy font-semibold"><Check className="w-5 h-5 text-paw-success" /> A percentage of every order, donated automatically.</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] bg-cover bg-center shadow-md" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1576201836106-db1758fd1c97?auto=format&fit=crop&w=800&q=80)" }} />
          </div>
        </div>
      </section>

      {/* EMOTIONAL BAND */}
      <section className="relative min-h-[440px] grid place-items-center text-center text-white overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-[center_35%]" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=1600&q=80)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,39,71,0.55), rgba(11,39,71,0.78))" }} />
        <div className="relative z-10 max-w-3xl px-6 py-20">
          <h2 className="font-slab text-3xl sm:text-5xl text-white">For the ones who'd do anything for their pet.</h2>
          <p className="mt-5 text-lg text-white/80 max-w-xl mx-auto">You'd never cut corners on their health. With PawLinx, you don't have to cut into your wallet either.</p>
          <button onClick={openEstimate} className="mt-7 inline-flex items-center gap-2 bg-white text-paw-primary rounded-full px-6 py-3.5 font-semibold hover:shadow-xl transition-all">Get your instant estimate <ArrowRight className="w-4 h-4" /></button>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-paw-cream-2 py-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">Loved by pet parents</span>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-extrabold tracking-tight text-paw-navy">Real savings, real peace of mind</h2>
          </div>
          <div className="mt-11 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white border border-paw-border rounded-3xl overflow-hidden shadow-sm">
                <div className="h-56 bg-cover bg-center" style={{ backgroundImage: `url(${t.img})` }} />
                <div className="p-6">
                  <Stars className="mb-3" />
                  <p className="text-[15px] text-paw-text leading-relaxed">"{t.quote}"</p>
                  <div className="flex items-center gap-3 mt-5 pt-4 border-t border-paw-border">
                    <div className="w-10 h-10 rounded-full bg-cover bg-center" style={{ backgroundImage: `url(${t.av})` }} />
                    <div>
                      <div className="text-sm font-bold text-paw-navy">{t.name}</div>
                      <div className="text-[12.5px] text-paw-text-muted">{t.who}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-paw-cream py-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="relative overflow-hidden rounded-[32px] bg-paw-primary text-white p-10 sm:p-14 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div className="absolute -right-24 -top-28 w-80 h-80 rounded-full bg-white/10" />
            {[["Up to 30%", "average savings"], ["12,000+", "pets helped"], ["4.9/5", "customer rating"], ["United States", "delivery"]].map(([b, s]) => (
              <div key={s} className="relative">
                <div className="text-4xl sm:text-[44px] font-extrabold tracking-tight">{b}</div>
                <div className="text-[#cfe0f2] text-sm mt-1">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECURE PAYMENTS — trust / Stripe / refunds */}
      <section className="bg-paw-cream-2 py-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary"><Lock className="w-4 h-4" /> Secure checkout</span>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-extrabold tracking-tight text-paw-navy">Your payment is in safe hands</h2>
            <p className="mt-4 text-[17px] text-paw-text-secondary">
              Every payment is processed securely by Stripe, one of the world's most trusted payment providers. PawLinx never sees, handles, or stores your card details. They are encrypted and handled entirely by Stripe.
            </p>
            <div className="mt-6 inline-flex items-center gap-2.5 bg-white border border-paw-border rounded-full pl-4 pr-5 py-2.5 shadow-sm">
              <span className="text-[13px] font-semibold text-paw-text-secondary">Payments powered by</span>
              <img src="/stripe-logo.svg" alt="Stripe" className="h-6 w-auto" />
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <div className="bg-white border border-paw-border rounded-3xl p-7 shadow-sm">
              <div className="w-12 h-12 rounded-2xl grid place-items-center bg-paw-blue-50 text-paw-primary mb-5"><Lock className="w-6 h-6" /></div>
              <h3 className="text-lg font-bold text-paw-navy mb-2">We never touch your card</h3>
              <p className="text-[14.5px] text-paw-text-secondary leading-relaxed">Checkout is hosted by Stripe. Your card number is entered directly into Stripe's secure, PCI-DSS certified system, not into PawLinx. We never see or save it.</p>
            </div>
            <div className="bg-white border border-paw-border rounded-3xl p-7 shadow-sm">
              <div className="w-12 h-12 rounded-2xl grid place-items-center bg-paw-blue-50 text-paw-primary mb-5"><ShieldCheck className="w-6 h-6" /></div>
              <h3 className="text-lg font-bold text-paw-navy mb-2">Bank-level encryption</h3>
              <p className="text-[14.5px] text-paw-text-secondary leading-relaxed">Stripe protects every transaction with end-to-end encryption and the same security trusted by millions of businesses worldwide, including Amazon and Google.</p>
            </div>
            <div className="bg-white border border-paw-border rounded-3xl p-7 shadow-sm">
              <div className="w-12 h-12 rounded-2xl grid place-items-center bg-paw-blue-50 text-paw-primary mb-5"><CreditCard className="w-6 h-6" /></div>
              <h3 className="text-lg font-bold text-paw-navy mb-2">All major cards welcome</h3>
              <p className="text-[14.5px] text-paw-text-secondary leading-relaxed">Pay with Visa, Mastercard, American Express, and more. Prices are shown and charged in US dollars, with no hidden fees or surprise conversions.</p>
            </div>
          </div>

          {/* Refund policy */}
          <div className="mt-5 grid lg:grid-cols-2 gap-5">
            <div className="bg-paw-navy text-white rounded-3xl p-8 shadow-sm">
              <div className="w-12 h-12 rounded-2xl grid place-items-center bg-white/10 text-paw-gold mb-5"><RotateCcw className="w-6 h-6" /></div>
              <h3 className="text-xl font-extrabold mb-2">Didn't arrive? Full refund.</h3>
              <p className="text-[14.5px] text-white/75 leading-relaxed">If your order is never delivered, you get a full refund, no questions asked. We track every shipment, and if something goes wrong on the way, you are not the one who pays for it.</p>
            </div>
            <div className="bg-white border border-paw-border rounded-3xl p-8 shadow-sm">
              <div className="w-12 h-12 rounded-2xl grid place-items-center bg-paw-blue-50 text-paw-primary mb-5"><PackageCheck className="w-6 h-6" /></div>
              <h3 className="text-xl font-extrabold text-paw-navy mb-2">Arrived damaged? We make it right.</h3>
              <p className="text-[14.5px] text-paw-text-secondary leading-relaxed">If your medication arrives damaged or defective, contact us and we'll arrange a replacement or a full refund. Just reach out to <a href="mailto:info@pawlinx.com" className="text-paw-primary font-semibold hover:underline">info@pawlinx.com</a> and we'll take care of you.</p>
            </div>
          </div>

          <p className="mt-6 text-center text-[13px] text-paw-text-muted max-w-2xl mx-auto">
            Refunds are issued to your original payment method through Stripe. Because prescriptions are dispensed specifically for your pet, refund eligibility focuses on delivery and product condition. See our <Link to="/terms" className="text-paw-primary hover:underline">Terms of Service</Link> for full details.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white py-20">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[13px] font-extrabold tracking-[0.08em] uppercase text-paw-primary">Questions</span>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-extrabold tracking-tight text-paw-navy">Everything you might be wondering</h2>
          </div>
          <Faq />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-paw-cream py-24">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="relative overflow-hidden rounded-[32px] grid lg:grid-cols-2 bg-paw-primary text-white">
            <div className="relative z-10 p-10 sm:p-14">
              <PiggyBank className="w-10 h-10 mb-4 text-white/90" />
              <h2 className="text-3xl sm:text-[38px] font-extrabold tracking-tight leading-tight">Ready to save on your pet's medication?</h2>
              <p className="mt-4 text-[17px] text-[#cfe0f2] max-w-md">Get a free, no commitment estimate in minutes. Vet reviewed prescriptions, delivered to your door across the United States.</p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <button onClick={openEstimate} className="inline-flex items-center justify-center gap-2 bg-white text-paw-navy rounded-full px-7 py-3.5 font-semibold hover:bg-paw-cream transition-all">
                  Get instant estimate <ArrowRight className="w-4 h-4" />
                </button>
                <Link to="/#how" className="inline-flex items-center justify-center gap-2 bg-paw-navy/30 border border-white/30 text-white rounded-full px-7 py-3.5 font-semibold hover:bg-paw-navy/50 transition-all">
                  See how it works
                </Link>
              </div>
            </div>
            <div className="relative min-h-[280px] bg-cover bg-center hidden lg:block" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1517849845537-4d257902454a?auto=format&fit=crop&w=1000&q=80)" }}>
              <div className="absolute inset-0 bg-gradient-to-r from-paw-primary via-paw-primary/30 to-transparent" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
